class ColorGaugeElement extends HTMLElement {
  constructor() {
    super();
    this.colors = [];
  }

  static get observedAttributes() {
    return ['colors'];
  }

  connectedCallback() {
    this.bindEvents();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'colors' && oldValue !== newValue) {
      this.colors = this.parseColors(newValue);
      this.updateGradient();
    }
  }

  parseColors(colorsJson) {
    try {
      return JSON.parse(colorsJson);
    } catch (e) {
      console.error('Error parsing colors:', e);
      return [];
    }
  }

  updateGradient() {
    if (this.colors.length === 0) return;

    const gradient = this.querySelector('.color-gauge-gradient');
    if (!gradient) return;

    const gradientStops = this.colors
      .map(color => `${color.code} ${color.position}%`)
      .join(', ');

    gradient.style.background = `linear-gradient(to right, ${gradientStops})`;
  }

  findClosestColor(mouseX) {
    if (this.colors.length === 0) return null;
    
    const gauge = this.querySelector('.color-gauge');
    if (!gauge) return null;
    
    const rect = gauge.getBoundingClientRect();
    const relativeX = mouseX - rect.left;
    const percentage = (relativeX / rect.width) * 100;
    
    let closestColor = this.colors[0];
    let minDistance = Math.abs(percentage - this.colors[0].position);
    
    for (let i = 1; i < this.colors.length; i++) {
      const distance = Math.abs(percentage - this.colors[i].position);
      if (distance < minDistance) {
        minDistance = distance;
        closestColor = this.colors[i];
      }
    }
    
    return closestColor;
  }

  updateTooltip(color) {
    if (!color) return;
    
    const tooltipName = this.querySelector('.color-tooltip-name');
    const tooltipCode = this.querySelector('.color-tooltip-code');
    const tooltipImage = this.querySelector('.color-tooltip-image');
    const tooltipLink = this.querySelector('.color-tooltip-link');
    
    if (tooltipName) tooltipName.textContent = color.name;
    if (tooltipCode) tooltipCode.textContent = color.code;
    
    if (tooltipImage) {
      if (color.image) {
        tooltipImage.style.backgroundImage = `url(${color.image})`;
        tooltipImage.style.backgroundSize = 'cover';
        tooltipImage.style.backgroundPosition = 'center';
      } else {
        tooltipImage.style.background = color.code;
      }
    }

    if (tooltipLink) {
      if (color.url != '') {
        tooltipLink.href = color.url;
        tooltipLink.removeAttribute('role');
        tooltipLink.removeAttribute('aria-disabled');
      } else {
        tooltipLink.removeAttribute('href');
        tooltipLink.setAttribute('role', 'link');
        tooltipLink.setAttribute('aria-disabled', 'false');
      }
    }
  }

  showTooltip(mouseX) {
    const color = this.findClosestColor(mouseX);
    if (!color) return;
    
    const gauge = this.querySelector('.color-gauge');
    const marker = this.querySelector('.color-marker');
    const tooltip = this.querySelector('.color-tooltip');
    
    if (!gauge || !marker || !tooltip) return;
    
    const rect = gauge.getBoundingClientRect();
    const relativeX = mouseX - rect.left;
    const percentage = (relativeX / rect.width) * 100;
    
    marker.style.left = `${percentage}%`;
    this.updateTooltip(color);
    tooltip.classList.add('show');
  }

  hideTooltip() {
    const tooltip = this.querySelector('.color-tooltip');
    if (tooltip) {
      tooltip.classList.remove('show');
    }
  }

  bindEvents() {
    const gauge = this.querySelector('.color-gauge');
    const tooltip = this.querySelector('.color-tooltip');

    gauge?.addEventListener('mousemove', (e) => {
      this.showTooltip(e.clientX);
    });

    tooltip?.addEventListener('mouseleave', () => {
      this.hideTooltip();
    });

    gauge?.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      this.showTooltip(touch.clientX);
    });

    gauge?.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      this.showTooltip(touch.clientX);
    });

    tooltip?.addEventListener('touchend', () => {
      this.hideTooltip();
    });
  }

  // Méthodes publiques pour l'API
  setColors(colors) {
    this.colors = colors;
    this.updateGradient();
  }

  getColors() {
    return this.colors;
  }

  // Événements personnalisés
  dispatchColorSelect(color) {
    this.dispatchEvent(new CustomEvent('colorSelect', {
      detail: { color },
      bubbles: true,
      composed: true
    }));
  }
}

// Enregistrer le web component
customElements.define('color-gauge', ColorGaugeElement);

// Classe utilitaire pour la compatibilité avec l'ancienne API
class ColorGauge {
  constructor(sectionId, colors) {
    this.sectionId = sectionId;
    this.element = document.getElementById(`color-gauge-${sectionId}`);
    
    if (this.element) {
      this.element.setAttribute('colors', JSON.stringify(colors));
    }
  }
}

// Fonction d'initialisation pour les sections de jauge de colorimétrie
window.initColorGauges = function() {
  // Cette fonction sera appelée depuis le template Liquid
  // pour initialiser les jauges avec les données spécifiques
}; 